/**
 * Specialty window shapes. Each shape lists the measurements it REQUIRES besides the
 * plain rectangle (width x height). `path` is an SVG path in a 100x100 box (y down) used
 * both for the picker icon and for the outline stamped on the annotated photo.
 *
 * Field keys: w (width/base), h (total height), leg (leg / spring-line height), top (top
 * width), legL / legR (left / right leg heights), d (diameter).
 */
export const SHAPES = [
  { id: "rectangle", label: "Rectangle", fields: ["w", "h"], path: "M2 2 H98 V98 H2 Z" },
  { id: "half_circle", label: "Half Circle", fields: ["w", "h"], auto: { h: (d) => half(d.w) },
    path: "M2 98 A48 48 0 0 1 98 98 Z" },
  { id: "quarter_circle", label: "Quarter Circle", fields: ["w", "h", "side"], path: "M2 98 V2 A96 96 0 0 1 98 98 Z" },
  { id: "eyebrow", label: "Eyebrow", fields: ["w", "h", "leg"], path: "M2 98 V78 Q50 -14 98 78 V98 Z" },
  { id: "springline", label: "Springline", fields: ["w", "h", "leg"], path: "M2 98 V60 A48 48 0 0 1 98 60 V98 Z" },
  { id: "elliptical", label: "Elliptical", fields: ["w", "h"], path: "M2 98 A48 96 0 0 1 98 98 Z" },
  { id: "equal_leg_triangle", label: "Equal Legs (Triangle)", fields: ["w", "h"], path: "M2 98 L50 2 L98 98 Z" },
  { id: "right_triangle", label: "Right Triangle", fields: ["w", "h", "side"], path: "M2 98 V2 L98 98 Z" },
  { id: "trapezoid", label: "Trapezoid", fields: ["w", "legL", "legR", "top"], path: "M2 98 V40 L98 10 V98 Z" },
  { id: "pentagon", label: "Pentagon (Peak)", fields: ["w", "h", "leg"], path: "M2 98 V50 L50 2 L98 50 V98 Z" },
  { id: "octagon", label: "Octagon", fields: ["w", "h"], path: "M30 2 H70 L98 30 V70 L70 98 H30 L2 70 V30 Z" },
  { id: "circle", label: "Circle", fields: ["d"], path: "M50 2 A48 48 0 1 1 49.9 2 Z" },
  { id: "oval", label: "Oval", fields: ["w", "h"], path: "M50 2 A48 48 0 1 1 49.9 2 Z", ovalIcon: true },
];

/**
 * Door CONFIGURATIONS (door-kind units): the door alone or with sidelights / transom. Field keys:
 * w·h = door slab opening, sl = single sidelight width (+ side), slL/slR = both sidelights, tr = transom height.
 * Paths are generated by shapePath() from the real proportions.
 */
export const DOOR_SHAPES = [
  { id: "door", label: "Door only", fields: ["w", "h"], door: {} },
  { id: "door_sl1", label: "Door + 1 sidelight", fields: ["w", "h", "sl", "side"], door: { sl1: true } },
  { id: "door_sl2", label: "Door + 2 sidelights", fields: ["w", "h", "slL", "slR"], door: { sl2: true } },
  { id: "door_tr", label: "Door + transom", fields: ["w", "h", "tr"], door: { tr: true } },
  { id: "door_tr_sl1", label: "Door + transom + 1 sidelight", fields: ["w", "h", "sl", "tr", "side"], door: { tr: true, sl1: true } },
  { id: "door_tr_sl2", label: "Door + transom + 2 sidelights", fields: ["w", "h", "slL", "slR", "tr"], door: { tr: true, sl2: true } },
  // split transoms: one above the door AND one above each sidelight (own heights)
  { id: "door_tr2_sl1", label: "Door + 1 sidelight + 2 transoms", fields: ["w", "h", "sl", "tr", "trS", "side"], door: { tr: true, sl1: true, split: true } },
  { id: "door_tr3_sl2", label: "Door + 2 sidelights + 3 transoms", fields: ["w", "h", "slL", "slR", "tr", "trL", "trR"], door: { tr: true, sl2: true, split: true } },
];
export const DOOR_SHAPE_IDS = new Set(DOOR_SHAPES.map((s) => s.id));
export const isDoorShape = (id) => DOOR_SHAPE_IDS.has(id);

export const SHAPE_BY_ID = Object.fromEntries([...SHAPES, ...DOOR_SHAPES].map((s) => [s.id, s]));

/** i18n keys for every field (the label text lives in i18n.js as measure_dim_<key>). */
export const FIELD_KEYS = ["w", "h", "leg", "top", "legL", "legR", "d", "side", "sl", "slL", "slR", "tr", "trL", "trR", "trS"];

/** Sidelight / transom widths as fractions of the WHOLE unit box, from the real inches (icon defaults otherwise). */
export function doorGeometry(shapeId, dims = {}) {
  const sh = SHAPE_BY_ID[shapeId];
  const cfg = (sh && sh.door) || {};
  // w / h are the WHOLE unit; sidelights and transoms are optional parts of it
  const W = parseInches(dims.w), H = parseInches(dims.h);
  let L = 0, R = 0, T = 0, TL = 0, TR = 0;
  const DEF_SL = 0.22, DEF_TR = 0.18;
  const fracW = (v) => (W && v ? v / W : DEF_SL);
  const fracH = (v) => (H && v ? v / H : DEF_TR);
  if (cfg.sl1) {
    const f = fracW(parseInches(dims.sl));
    if ((dims.side || "L") === "R") R = f; else L = f;
  } else if (cfg.sl2) {
    L = fracW(parseInches(dims.slL)); R = fracW(parseInches(dims.slR));
  }
  if (cfg.tr) {
    T = fracH(parseInches(dims.tr));
    if (cfg.split) {
      const right = (dims.side || "L") === "R";
      if (L) TL = fracH(cfg.sl2 ? parseInches(dims.trL) : (!right ? parseInches(dims.trS) : null));
      if (R) TR = fracH(cfg.sl2 ? parseInches(dims.trR) : (right ? parseInches(dims.trS) : null));
    }
  }
  const clamp = (v) => Math.min(0.45, Math.max(0.06, v));
  return { L: L ? clamp(L) : 0, R: R ? clamp(R) : 0, T: T ? clamp(T) : 0, TL: TL ? clamp(TL) : 0, TR: TR ? clamp(TR) : 0, split: !!cfg.split };
}

/** `35 1/2"` → 17 3/4"  (half of a fraction-ish inch string; empty when unparsable) */
function half(txt) {
  const v = parseInches(txt);
  return v == null ? "" : formatInches(v / 2);
}

export function parseInches(txt) {
  if (!txt) return null;
  const s = String(txt).replace(/[″"in]+/gi, " ").replace(/-/g, " ").trim();
  const m = s.match(/^(\d+)(?:\s+(\d+)\s*\/\s*(\d+))?$/) || s.match(/^(\d+)\.(\d+)$/);
  if (!m) return null;
  if (s.includes(".")) return parseFloat(s);
  const whole = parseInt(m[1], 10);
  const frac = m[2] && m[3] ? parseInt(m[2], 10) / parseInt(m[3], 10) : 0;
  return whole + frac;
}

export function formatInches(v) {
  const whole = Math.floor(v);
  const frac = Math.round((v - whole) * 16);
  if (frac === 0) return `${whole}"`;
  if (frac === 16) return `${whole + 1}"`;
  let n = frac, d = 16;
  while (n % 2 === 0) { n /= 2; d /= 2; }
  return `${whole ? `${whole} ` : ""}${n}/${d}"`;
}

/** Width/height the PDF match + arrows should use for a shape (circle → diameter both ways). */
export function shapeWH(shapeId, dims) {
  if (shapeId === "circle") return { w: dims.d || "", h: dims.d || "" };

  if (shapeId === "trapezoid") {
    const l = parseInches(dims.legL), r = parseInches(dims.legR);
    const h = l != null && r != null ? formatInches(Math.max(l, r)) : (dims.legL || dims.legR || "");
    return { w: dims.w || "", h };
  }
  return { w: dims.w || "", h: dims.h || "" };
}

/** Extra dims (besides W/H) as a short text: `Leg 12" · Top 24"`. */
export function shapeExtraText(shapeId, dims, label = (k) => k) {
  const sh = SHAPE_BY_ID[shapeId];
  if (!sh) return "";
  const parts = [];
  for (const k of sh.fields) {
    if (k === "w" || k === "h" || k === "d") continue;
    if (k === "side") { if (dims.side) parts.push(dims.side === "L" ? label("side_left") : label("side_right")); continue; }
    if (dims[k]) parts.push(`${label(k)} ${dims[k]}`);
  }
  return parts.join(" · ");
}

/** Fields that may stay empty: top width, side, and every door part (sidelights / transoms) — the whole size is enough. */
export const OPTIONAL_KEYS = new Set(["side", "top", "sl", "slL", "slR", "tr", "trL", "trR", "trS"]);
export function shapeComplete(shapeId, dims) {
  const sh = SHAPE_BY_ID[shapeId];
  if (!sh) return false;
  return sh.fields.every((k) => (OPTIONAL_KEYS.has(k) ? true : !!(dims[k] || "").trim()));
}

/** Where the legs end, as a fraction of the total height, taken from the REAL measurements
 *  (leg ÷ height); falls back to the icon proportions when the numbers are not typed yet. */
const LEG_DEFAULT = { springline: 38 / 96, eyebrow: 20 / 96, pentagon: 48 / 96 };
export function shapeGeometry(shapeId, dims = {}) {
  const clamp = (v) => Math.min(0.92, Math.max(0.04, v));
  if (shapeId === "trapezoid") {
    const l = parseInches(dims.legL), r = parseInches(dims.legR);
    const mx = l != null && r != null ? Math.max(l, r) : null;
    return {
      legL: mx ? clamp(l / mx) : 58 / 96,
      legR: mx ? clamp(r / mx) : 88 / 96,
    };
  }
  const h = parseInches(dims.h), leg = parseInches(dims.leg);
  const frac = h && leg != null && leg < h ? clamp(leg / h) : LEG_DEFAULT[shapeId];
  return { leg: frac };
}

/** SVG path (100x100 box) whose angles follow the measured leg / height ratio. */
export function shapePathFor(shapeId, dims) {
  const sh = SHAPE_BY_ID[shapeId];
  return sh ? shapePath(sh, dims || {}) : "";
}

function shapePath(sh, dims) {
  // on the photo the arc must reach the top of the box (full height), whatever the box aspect
  if (sh.id === "half_circle") return "M2 98 A48 96 0 0 1 98 98 Z";
  if (sh.door) {
    const { L, R, T, TL, TR, split } = doorGeometry(sh.id, dims);
    const xL = +(2 + 96 * L).toFixed(2), xR = +(98 - 96 * R).toFixed(2), yT = +(2 + 96 * T).toFixed(2);
    let d = "M2 2 H98 V98 H2 Z";
    if (split) {
      if (L) d += ` M${xL} 2 V98 M2 ${+(2 + 96 * TL).toFixed(2)} H${xL}`;
      if (R) d += ` M${xR} 2 V98 M${xR} ${+(2 + 96 * TR).toFixed(2)} H98`;
      if (T) d += ` M${xL} ${yT} H${xR}`;
    } else {
      if (L) d += ` M${xL} ${yT} V98`;
      if (R) d += ` M${xR} ${yT} V98`;
      if (T) d += ` M2 ${yT} H98`;
    }
    const kx = R ? xR - 6 : 92; // knob on the lock side of the slab
    d += ` M${kx} ${(yT + 98) / 2} h0.01`;
    return d;
  }
  const g = shapeGeometry(sh.id, dims);
  if (g.leg != null && LEG_DEFAULT[sh.id] != null) {
    const ly = +(98 - 96 * g.leg).toFixed(2);
    const rise = +(ly - 2).toFixed(2);
    if (sh.id === "pentagon") return `M2 98 V${ly} L50 2 L98 ${ly} V98 Z`;
    return `M2 98 V${ly} A48 ${rise} 0 0 1 98 ${ly} V98 Z`;
  }
  if (sh.id === "trapezoid") {
    const yl = +(98 - 96 * g.legL).toFixed(2), yr = +(98 - 96 * g.legR).toFixed(2);
    return `M2 98 V${yl} L98 ${yr} V98 Z`;
  }
  return sh.path;
}

/** Stroke the shape outline inside a box on a canvas (box in px) using the ctx's current
 *  strokeStyle / lineWidth. Mirrors quarter circle / right triangle when side = "R". */
export function strokeShapeOutline(ctx, shapeId, bx, by, bw, bh, side, dims) {
  const sh = SHAPE_BY_ID[shapeId];
  if (!sh || shapeId === "rectangle" || typeof Path2D === "undefined" || typeof DOMMatrix === "undefined") return;
  const flip = side === "R" && (shapeId === "quarter_circle" || shapeId === "right_triangle");
  if (sh.door) {
    const d = dims || {};
    const m0 = new DOMMatrix().translate(bx, by).scale(bw / 100, bh / 100);
    const p0 = new Path2D();
    p0.addPath(new Path2D(shapePath(sh, { ...d, side: side || d.side })), m0);
    ctx.save(); ctx.lineJoin = "round"; ctx.stroke(p0); ctx.restore();
    return;
  }
  const m = flip
    ? new DOMMatrix().translate(bx + bw, by).scale(-bw / 100, bh / 100)
    : new DOMMatrix().translate(bx, by).scale(bw / 100, bh / 100);
  const p = new Path2D();
  p.addPath(new Path2D(shapePath(sh, dims || {})), m);
  ctx.save();
  ctx.lineJoin = "round";
  ctx.stroke(p);
  ctx.restore();
}
